<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import appConfig from "../../../app.config";
import axios from 'axios';
import Swal from "sweetalert2";
import moment from 'moment';
import { usePermissions } from "@/composables/usePermissions";
import FulfillmentSubStatusSelect from './order-fulfillment-substatus-select.vue'

// IMS lookup settings
const LOOKUP_MIN_LENGTH = 5;   // don't auto-search very short input
const LOOKUP_DEBOUNCE_MS = 600;

// Fulfillment status ids used on this page
const STATUS_PENDING_ALLOCATION = 1;
const STATUS_LOCKED = [3,4,5];

export default {
    page: {
        title: "Fulfilment Details",
        meta: [{ name: "description", content: appConfig.description }],
    },

    components: {
        Layout,
        PageHeader,
        FulfillmentSubStatusSelect,
    },

    setup() {
        const { canReadOrderFulfillment } = usePermissions();
        return {
            canReadOrderFulfillment,
        };
    },

    data() {
        return {
            orderIdParam: this.$route.params.id,
            title: "Fulfilment Details",
            items: [
                {
                    text: "Order Fulfilment",
                    href: "/orders/order-fulfillment",
                },
                {
                    text: "Details",
                    active: true,
                },
            ],
            loading: true,
            orderDetail: null,
            imei1: '',
            imei2: '',
            serialNo: '',
            trackingNo: '',
            subStatus: null,
            currentSubStatusName: null,
            updateLoading: false,
            updateErrors: {},
            reverseLoading: false,

            // IMS lookup
            lookupKeyword: '',
            lookupLoading: false,
            lookupStatus: null,     // null | 'found' | 'not_found' | 'error'
            lookupMessage: '',
            lookupTimer: null,
            lookupController: null,
            lastLookedUp: '',
        };
    },

    computed: {
        isLocked() {
            return this.orderDetail && STATUS_LOCKED.includes(this.orderDetail.order_fulfillment_status_id);
        },
        canReverse() {
            return this.orderDetail
                && !STATUS_LOCKED.includes(this.orderDetail.order_fulfillment_status_id)
                && this.orderDetail.order_fulfillment_status_id !== STATUS_PENDING_ALLOCATION;
        },
    },

    created() {
        this.fetchOrderDetails(true);
    },

    beforeUnmount() {
        clearTimeout(this.lookupTimer);
        if (this.lookupController) this.lookupController.abort();
    },

    methods: {
        format_date(value) {
            if (value) {
                return moment(String(value)).format('YYYY-MM-DD HH:mm:ss');
            }
            return '---';
        },

        formatFullAddress(detail) {
            if (!detail) return '---';
            const parts = [
                detail.delivery_address,
                detail.delivery_postcode,
                detail.delivery_city,
                detail.delivery_state
            ].filter(p => p && String(p).trim() !== '' && String(p).trim() !== 'null');

            return parts.length > 0 ? parts.join(', ') : (detail.delivery_address || '---');
        },

        errorMessage(error, fallback) {
            return (error.response && error.response.data && error.response.data.message) || fallback;
        },

        fieldError(field) {
            const messages = this.updateErrors[field];
            return messages && messages.length ? messages[0] : '';
        },

        // Called on every keystroke: wait until the user stops typing
        onLookupInput() {
            clearTimeout(this.lookupTimer);
            this.lookupStatus = null;
            this.lookupMessage = '';

            const keyword = this.lookupKeyword.trim();
            if (keyword.length < LOOKUP_MIN_LENGTH) return;

            this.lookupTimer = setTimeout(() => this.lookupDevice(), LOOKUP_DEBOUNCE_MS);
        },

        // Called on Enter (also what barcode scanners send): search immediately
        onLookupEnter() {
            clearTimeout(this.lookupTimer);
            this.lookupDevice(true);
        },

        lookupDevice(force = false) {
            const keyword = this.lookupKeyword.trim();
            if (!keyword) return;

            // Skip repeat searches for the same value unless Enter was pressed
            if (!force && keyword === this.lastLookedUp) return;
            this.lastLookedUp = keyword;

            // Cancel a request still in flight
            if (this.lookupController) this.lookupController.abort();
            this.lookupController = new AbortController();

            this.lookupLoading = true;
            this.lookupStatus = null;
            this.lookupMessage = '';

            axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
            axios.get(process.env.VUE_APP_API_BASEURL + 'order-fulfillment/ims/lookup', {
                params: { keyword },
                signal: this.lookupController.signal,
            })
                .then(response => {
                    const device = response.data && response.data.data;

                    if (!device) {
                        this.setLookupNotFound();
                        return;
                    }

                    // Map IMS fields (adjust names to match your API)
                    this.imei1 = device.imei1 || device.imei_1 || '';
                    this.imei2 = device.imei2 || device.imei_2 || '';
                    this.serialNo = device.serialNo || device.serial_no || '';

                    // Clear old validation errors on the populated fields
                    const { ...rest } = this.updateErrors;
                    this.updateErrors = rest;

                    this.lookupStatus = 'found';
                    this.lookupMessage = 'Device found. Hardware identifiers have been filled in.';
                })
                .catch(error => {
                    if (axios.isCancel(error)) return;

                    if (error.response && error.response.status === 404) {
                        this.setLookupNotFound();
                        return;
                    }

                    console.error('Error looking up device in IMS:', error);
                    this.lookupStatus = 'error';
                    this.lookupMessage = this.errorMessage(error, 'Could not reach IMS. Please try again.');
                })
                .finally(() => {
                    this.lookupLoading = false;
                });
        },

        setLookupNotFound() {
            this.lookupStatus = 'not_found';
            this.lookupMessage = 'IMEI / serial no does not exist in IMS.';
        },

        fetchOrderDetails(showLoading = true) {
            if(showLoading){
                this.loading = true;
            }

            const targetId = this.$route.params.id;

            if (!targetId) {
                this.loading = false;
                this.orderDetail = null;
                return;
            }

            axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
            axios.get(process.env.VUE_APP_API_BASEURL + 'order-fulfillment/' + targetId)
                .then(response => {
                    if (response && response.data && response.data.data) {
                        const data = response.data.data;
                        this.orderDetail = data;
                        this.imei1 = data.imei;
                        this.imei2 = data.imei_2;
                        this.serialNo = data.serial_number;
                        this.trackingNo = data.fulfillment_tracking_no;
                        // The select matches this name to pre-select the saved remark
                        this.currentSubStatusName = data.fulfillment_substatus || null;
                    } else {
                        this.orderDetail = null;
                    }
                })
                .catch(error => {
                    console.error('Error fetching order fulfillment details:', error);
                    this.orderDetail = null;
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        updateImeis() {
            const targetId = this.$route.params.id;
            this.updateLoading = true;
            this.updateErrors = {};

            const postData = {
                imei1: this.imei1,
                imei2: this.imei2,
                serialNo: this.serialNo,
                trackingNo: this.trackingNo,
                fulfillmentSubStatus: this.subStatus,
            };

            axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
            axios.patch(process.env.VUE_APP_API_BASEURL + 'order-fulfillment/' + targetId, postData)
                .then(() => {
                    this.fetchOrderDetails(false);
                    Swal.fire({
                        icon: 'success',
                        title: 'Updated',
                        text: 'Hardware details have been updated successfully.',
                        timer: 2000,
                        showConfirmButton: false,
                    });
                })
                .catch(error => {
                    console.error('Error pushing order fulfillment details:', error);

                    // Validation errors are shown under each field
                    if (error.response && error.response.status === 422) {
                        this.updateErrors = error.response.data.errors || {};
                        Swal.fire({
                            icon: 'warning',
                            title: 'Please check the form',
                            text: 'Some fields are invalid. See the messages under each field.',
                        });
                        return;
                    }

                    Swal.fire({
                        icon: 'error',
                        title: 'Update failed',
                        text: this.errorMessage(error, 'Could not update the hardware details.'),
                    });
                })
                .finally(() => {
                    this.updateLoading = false;
                });
        },

        async reverseToPendingAllocation() {
            if (!this.canReverse) return;

            const result = await Swal.fire({
                icon: 'warning',
                title: 'Reverse fulfilment?',
                text: 'This fulfilment will be moved back to Pending Allocation.',
                showCancelButton: true,
                confirmButtonText: 'Yes, reverse it',
                cancelButtonText: 'Cancel',
                reverseButtons: true,
                focusCancel: true,
            });
            if (!result.isConfirmed) return;

            const targetId = this.$route.params.id;
            this.reverseLoading = true;

            axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
            axios.patch(process.env.VUE_APP_API_BASEURL + 'order-fulfillment/' + targetId + '/reverse')
                .then(() => {
                    this.fetchOrderDetails(false);
                    Swal.fire({
                        icon: 'success',
                        title: 'Reversed',
                        text: 'Fulfilment status has been reversed to Pending Allocation.',
                        timer: 2000,
                        showConfirmButton: false,
                    });
                })
                .catch(error => {
                    console.error('Error reversing order fulfillment status:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Reverse failed',
                        text: this.errorMessage(error, 'Could not reverse the fulfilment status.'),
                    });
                })
                .finally(() => {
                    this.reverseLoading = false;
                });
        },
    },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <!-- Main Container if user has read permission -->
    <div v-if="canReadOrderFulfillment">
        <!-- Top Back Navigation Bar -->
        <div class="row mb-3">
            <div class="col-12 d-flex justify-content-between align-items-center">
                <router-link to="/orders/order-fulfillment" class="btn btn-outline-secondary btn-sm-md">
                    <i class="ri-arrow-left-line align-middle me-1"></i>
                    <span class="d-none d-sm-inline">Back to Fulfilment List</span>
                    <span class="d-inline d-sm-none">Back</span>
                </router-link>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <p class="text-muted mt-2">Loading fulfilment details...</p>
        </div>

        <div v-else-if="orderDetail">
            <!-- Header Summary Card -->
            <div class="card mb-3 border-0 shadow-sm">
                <div class="card-body p-3 p-sm-4">
                    <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
                        <div class="d-flex align-items-center">
                            <div class="avatar-sm avatar-sm-md bg-soft-primary rounded-circle d-flex align-items-center justify-content-center me-2 me-sm-3 flex-shrink-0">
                                <i class="ri-file-list-3-line text-primary font-size-20"></i>
                            </div>
                            <div>
                                <h5 class="mb-1 text-dark text-break header-ref-title">
                                    Fulfilment Reference: <span class="font-monospace text-primary fw-bold">{{ orderDetail.order_number || orderDetail.orderId }}</span>
                                </h5>
                                <div class="text-muted font-size-12 d-flex flex-wrap align-items-center gap-2">
                                    <span><i class="ri-calendar-line me-1"></i> Placed on {{ format_date(orderDetail.created_at) }}</span>
                                    <span class="d-none d-sm-inline">&bull;</span>
                                    <span><i class="ri-user-line me-1"></i> {{ orderDetail.customer_name }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="d-flex flex-wrap align-items-center gap-2">
                            <span 
                                class="badge font-size-12 px-2 py-1 px-sm-3 py-sm-2" 
                                :class="orderDetail.order_fulfillment_status_id !== 1 ? 'bg-soft-success text-success' : 'bg-soft-warning text-warning'"
                            >
                                <i class="ri-checkbox-circle-line me-1" v-if="orderDetail.order_status_name === 'Processed'"></i>
                                <i class="ri-time-line me-1" v-else></i>
                                Status: {{ orderDetail.order_fulfillment_status }}
                            </span>

                            <!-- Reverse to Pending Allocation -->
                            <button
                                v-if="!isLocked"
                                type="button"
                                class="btn btn-outline-danger btn-sm"
                                :disabled="!canReverse || reverseLoading"
                                @click="reverseToPendingAllocation"
                            >
                                <span v-if="reverseLoading" class="spinner-border spinner-border-sm me-1" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                </span>
                                <i v-else class="ri-arrow-go-back-line me-1"></i>
                                Reverse to Pending Allocation
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Details Section Grid -->
            <div class="row g-3">
                <!-- 1. Order Overview Card -->
                <div class="col-md-6 col-xl-4">
                    <div class="card h-100 border-0 shadow-sm">
                        <div class="card-header bg-light py-3">
                            <h6 class="card-title mb-0 text-primary">
                                <i class="ri-shopping-bag-3-line me-1 align-middle"></i> Order Details
                            </h6>
                        </div>
                        <div class="card-body">
                            <div class="mb-3">
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Order ID</label>
                                <span class="font-monospace fw-bold font-size-14 text-dark">{{ orderDetail.order_number || orderDetail.orderId }}</span>
                            </div>
                            <div class="mb-3">
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Order Status</label>
                                <span class="badge" :class="orderDetail.order_status_name === 'Processed' ? 'bg-soft-success text-success' : 'bg-soft-warning text-warning'">
                                    {{ orderDetail.order_status_name }}
                                </span>
                            </div>
                            <div class="mb-3">
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Order Date & Time</label>
                                <span class="text-dark font-size-13 fw-medium">{{ format_date(orderDetail.created_at) }}</span>
                            </div>
                            <div>
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Program Name</label>
                                <span class="fw-medium font-size-13 text-dark">{{ orderDetail.program_name || '---' }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 2. Customer Details Card -->
                <div class="col-md-6 col-xl-4">
                    <div class="card h-100 border-0 shadow-sm">
                        <div class="card-header bg-light py-3">
                            <h6 class="card-title mb-0 text-primary">
                                <i class="ri-user-3-line me-1 align-middle"></i> Customer Details
                            </h6>
                        </div>
                        <div class="card-body">
                            <div class="mb-3">
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Customer Full Name</label>
                                <span class="fw-bold font-size-14 text-dark">{{ orderDetail.customer_name }}</span>
                            </div>
                            <!-- <div class="mb-3">
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">NRIC Number</label>
                                <span class="font-monospace text-muted font-size-13">{{ orderDetail.nric || '---' }}</span>
                            </div> -->
                            <div class="mb-3">
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Phone Number</label>
                                <span class="font-size-13">
                                    <i class="ri-phone-line me-1 text-success"></i>
                                    <a :href="'tel:' + orderDetail.phone" class="text-dark fw-medium">{{ orderDetail.phone || '---' }}</a>
                                </span>
                            </div>
                            <!-- <div>
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Email Address</label>
                                <span class="font-size-13 text-break">
                                    <i class="ri-mail-line me-1 text-info"></i>
                                    <a :href="'mailto:' + orderDetail.email" class="text-muted">{{ orderDetail.email || '---' }}</a>
                                </span>
                            </div> -->
                        </div>
                    </div>
                </div>

                <!-- 3. Delivery Information Card -->
                <div class="col-md-12 col-xl-4">
                    <div class="card h-100 border-0 shadow-sm">
                        <div class="card-header bg-light py-3">
                            <h6 class="card-title mb-0 text-primary">
                                <i class="ri-truck-line me-1 align-middle"></i> Delivery Information
                            </h6>
                        </div>
                        <div class="card-body">
                            <div class="mb-3">
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Application Source</label>
                                <span 
                                    class="badge font-size-11 px-2 py-1"
                                    :class="String(orderDetail.application_source || orderDetail.program_type).toUpperCase() === 'ONLINE' ? 'bg-soft-success text-success' : 'bg-soft-warning text-warning'"
                                >
                                    <i :class="String(orderDetail.application_source || orderDetail.program_type).toUpperCase() === 'ONLINE' ? 'ri-global-line me-1' : 'ri-store-2-line me-1'"></i>
                                    {{ String(orderDetail.application_source || orderDetail.program_type || 'ONLINE').toUpperCase() }}
                                </span>
                            </div>
                            <div class="mb-3">
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Delivery Method</label>
                                <span class="badge bg-soft-primary text-primary font-size-12 px-2 py-1">
                                    <i :class="orderDetail.delivery_type === 'Home Delivery' ? 'ri-home-4-line me-1' : 'ri-store-3-line me-1'"></i>
                                    {{ orderDetail.delivery_type || 'Store Pickup' }}
                                </span>
                            </div>

                            <!-- If Store Pickup -->
                            <div v-if="orderDetail.delivery_type !== 'Home Delivery'">
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Pickup Branch</label>
                                <div class="fw-semibold text-dark font-size-13 mb-1">
                                    <i class="ri-building-4-line me-1 text-primary"></i>
                                    {{ orderDetail.pickup_branch || orderDetail.branchname || '---' }}
                                </div>
                                <small class="text-muted d-block" v-if="orderDetail.delivery_address">
                                    {{ orderDetail.delivery_address }}
                                </small>
                            </div>

                            <!-- If Home Delivery -->
                            <div v-else>
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Full Shipping Address</label>
                                <div class="p-2 bg-light rounded text-dark font-size-12 border">
                                    <i class="ri-map-pin-line me-1 text-danger"></i>
                                    {{ formatFullAddress(orderDetail) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 4. Product & Hardware Specification Card -->
                <div class="col-md-12 col-xl-12">
                    <div class="card h-100 border-0 shadow-sm">
                        <div class="card-header bg-light py-3">
                            <h6 class="card-title mb-0 text-primary">
                                <i class="ri-smartphone-line me-1 align-middle"></i> Product & Device Details
                            </h6>
                        </div>
                        <div class="card-body">
                            <div class="row g-3">
                                <div class="col-md-4">
                                    <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Product Name</label>
                                    <span class="fw-bold text-dark font-size-15">{{ orderDetail.product_name }}</span>
                                </div>
                                <div class="col-md-4">
                                    <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">SKU</label>
                                    <span class="badge bg-dark font-size-12">{{ orderDetail.sku }}</span>
                                </div>
                                <div class="col-md-4">
                                    <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Variant (Color / Storage)</label>
                                    <span class="fw-medium font-size-13">{{ orderDetail.color || '---' }} / {{ orderDetail.storage || '---' }}</span>
                                </div>                                
                                <!-- IMS lookup: type or scan an IMEI / serial no -->
                                <div class="col-md-8">
                                    <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1" for="lookupKeyword">
                                        Check IMEI / Serial No
                                    </label>
                                    <div class="input-group has-validation">
                                        <span class="input-group-text">
                                            <span v-if="lookupLoading" class="spinner-border spinner-border-sm text-primary" role="status">
                                                <span class="visually-hidden">Searching...</span>
                                            </span>
                                            <i v-else class="ri-search-line"></i>
                                        </span>
                                        <input
                                            v-model="lookupKeyword"
                                            type="text"
                                            class="form-control"
                                            :class="{
                                                'is-invalid': lookupStatus === 'not_found' || lookupStatus === 'error',
                                                'is-valid': lookupStatus === 'found',
                                            }"
                                            id="lookupKeyword"
                                            placeholder="Scan or type IMEI / serial no, then press Enter"
                                            autocomplete="off"
                                            @input="onLookupInput"
                                            @keydown.enter.prevent="onLookupEnter"
                                        />
                                        <div class="invalid-feedback">{{ lookupMessage }}</div>
                                        <div class="valid-feedback">{{ lookupMessage }}</div>
                                    </div>
                                </div>

                                <!-- Fulfilment Status Remarks (editable even when status is 3) -->
                                <FulfillmentSubStatusSelect
                                    v-model="subStatus"
                                    :selected-label="currentSubStatusName"
                                    endpoint="order-fulfillment/substatus"
                                    :status-id="1"
                                    value-key="id"
                                    label-key="name"
                                />

                                <!-- Hardware Identifiers (IMEI 1, IMEI 2, SN) -->
                                <div class="col-12 mt-3">
                                    <div class="p-3 bg-light rounded border">
                                        <h6 class="text-muted font-size-12 text-uppercase fw-bold mb-2">
                                             <i class="ri-barcode-line me-1"></i> Hardware Identifiers
                                        </h6>
                                        <div class="row g-3">
                                            <div class="col-md-3">
                                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1" for="imei1">IMEI 1</label>
                                                <input v-model="imei1" type="text" class="form-control" :class="{ 'is-invalid': fieldError('imei1') }" id="imei1" readonly placeholder="Filled from IMS" />
                                                <div class="invalid-feedback">{{ fieldError('imei1') }}</div>
                                            </div>
                                            <div class="col-md-3">
                                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1" for="imei2">IMEI 2</label>
                                                <input v-model="imei2" type="text" class="form-control" :class="{ 'is-invalid': fieldError('imei2') }" id="imei2" readonly placeholder="Filled from IMS" />
                                                <div class="invalid-feedback">{{ fieldError('imei2') }}</div>
                                            </div>
                                            <div class="col-md-3">
                                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1" for="serialNo">Serial Number (SN)</label>
                                                <input v-model="serialNo" type="text" class="form-control" :class="{ 'is-invalid': fieldError('serialNo') }" id="serialNo" readonly placeholder="Filled from IMS" />
                                                <div class="invalid-feedback">{{ fieldError('serialNo') }}</div>
                                            </div>
                                            <div class="col-md-3">
                                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1" for="trackingNo">Tracking No</label>
                                                <input v-model="trackingNo" type="text" class="form-control" :class="{ 'is-invalid': fieldError('trackingNo') }" id="trackingNo" :disabled="isLocked" />
                                                <div class="invalid-feedback">{{ fieldError('trackingNo') }}</div>
                                            </div>
                                        </div>
                                        <div class="d-flex justify-content-end mt-2 gap-2">
                                            <button 
                                                :disabled="updateLoading" 
                                                type="button" 
                                                class="btn btn-primary"
                                                @click="updateImeis">
                                                <div v-if="updateLoading" class="spinner-border spinner-border-sm text-white" role="status">
                                                    <span class="visually-hidden">Loading...</span>
                                                </div>                                                
                                                Update
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 5. Payment Details Card -->
                <!-- <div class="col-md-12 col-xl-4">
                    <div class="card h-100 border-0 shadow-sm">
                        <div class="card-header bg-light py-3">
                            <h6 class="card-title mb-0 text-primary">
                                <i class="ri-bank-card-line me-1 align-middle"></i> Payment Details
                            </h6>
                        </div>
                        <div class="card-body">
                            <div class="mb-3">
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">First Payment Date</label>
                                <span class="text-dark font-size-13 fw-medium">{{ format_date(orderDetail.first_payment_date) }}</span>
                            </div>

                            <div class="mb-3" v-if="orderDetail.payment_status && (orderDetail.payment_status.bank_name || orderDetail.payment_status.bank_code)">
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Bank Name</label>
                                <span class="fw-semibold font-size-13 text-dark">
                                    <i class="ri-bank-line me-1 text-secondary"></i>
                                    {{ orderDetail.payment_status.bank_name || orderDetail.payment_status.bank_code }}
                                </span>
                            </div>

                            <div class="mb-3" v-if="orderDetail.payment_status">
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Payment Status</label>
                                <div class="d-flex flex-wrap gap-2">
                                    <span 
                                        class="badge font-size-11 px-2 py-1"
                                        :class="orderDetail.payment_status.is_mandate_paid ? 'bg-soft-success text-success' : 'bg-soft-danger text-danger'"
                                    >
                                        Mandate: {{ orderDetail.payment_status.is_mandate_paid ? 'Paid' : 'Unpaid' }}
                                    </span>
                                    <span 
                                        class="badge font-size-11 px-2 py-1"
                                        :class="orderDetail.payment_status.is_instant_pay_paid ? 'bg-soft-success text-success' : 'bg-soft-danger text-danger'"
                                    >
                                        InstantPay: {{ orderDetail.payment_status.is_instant_pay_paid ? 'Paid' : 'Unpaid' }}
                                    </span>
                                </div>
                            </div>

                            <div class="mb-3">
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Curlec / Gateway Ref ID</label>
                                <span class="font-monospace text-primary fw-semibold font-size-13">
                                    {{ orderDetail.curlec_reference_number || orderDetail.referenceNumber || '---' }}
                                </span>
                            </div>
                            <div>
                                <label class="text-muted font-size-11 text-uppercase fw-semibold d-block mb-1">Advance / Program Fee</label>
                                <span class="fw-bold text-success font-size-14">
                                    RM {{ orderDetail.program_fee || '0.00' }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div> -->
            </div>
        </div>

        <div v-else class="text-center p-5">
            <i class="ri-search-eye-line text-muted font-size-48 mb-3"></i>
            <h5>Order Not Found</h5>
            <p class="text-muted">The requested order details could not be loaded.</p>
            <router-link to="/orders/order-fulfillment" class="btn btn-primary">Back to Fulfilment List</router-link>
        </div>
    </div>

    <!-- Permission Denied Warning -->
    <div v-else class="card">
        <div class="card-body text-center text-muted p-5">
            <div class="mb-3">
                <i class="ri-shield-keyhole-line text-warning font-size-48"></i>
            </div>
            <h5>Access Denied</h5>
            <p class="text-muted mb-0">You do not have permission to view the Order Fulfilment details.</p>
        </div>
    </div>
  </Layout>
</template>

<style scoped>
@media (max-width: 576px) {
    .header-ref-title {
        font-size: 15px !important;
        line-height: 1.4 !important;
    }
    .header-ref-title span {
        display: inline-block;
        font-size: 14px !important;
    }
    .card-title {
        font-size: 13px !important;
    }
}
</style>